import React from 'react';

/**
 * FleetOverviewDashboard - Top status bar (Stitch style)
 * Horizontal row of stat chips: total, connected, flying, avg battery, low battery, GPS issues.
 */
function FleetOverviewDashboard({ drones = [], telemetryData = {} }) {
    const totalDrones = drones.length;
    const connectedDrones = drones.filter(d => d.status === 'connected').length;
    const activeDrones = Object.values(telemetryData).filter(t => t.armed).length;

    const connectedTelemetry = drones
        .filter(d => d.status === 'connected')
        .map(d => telemetryData[d.id])
        .filter(Boolean);

    const lowBatteryDrones = connectedTelemetry.filter(t =>
        t.battery_remaining && t.battery_remaining < 20
    ).length;

    const avgBattery = connectedTelemetry.length > 0
        ? connectedTelemetry.reduce((sum, t) => sum + (t.battery_remaining || 0), 0) / connectedTelemetry.length
        : 0;

    const gpsIssues = connectedTelemetry.filter(t => {
        const hasBadGpsFix = t.gps_fix === undefined || t.gps_fix === null || t.gps_fix < 3;
        const hasLowSatellites = t.satellites === undefined || t.satellites === null || t.satellites < 6;
        return hasBadGpsFix || hasLowSatellites;
    }).length;

    const StatChip = ({ label, value, icon, color, iconColor }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'color-mix(in srgb, var(--bgTertiary) 40%, transparent)',
            border: '1px solid color-mix(in srgb, var(--borderSecondary) 25%, transparent)',
            borderRadius: '8px',
            padding: '8px 16px',
            minWidth: 0,
            flex: '1 1 0',
        }}>
            <span className="material-symbols-outlined" style={{ color: iconColor || 'var(--accentSecondary)', fontSize: '24px', flexShrink: 0 }}>
                {icon}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{
                    fontFamily: 'var(--font-label)',
                    fontSize: '10px',
                    color: 'var(--textSecondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    whiteSpace: 'nowrap',
                }}>
                    {label}
                </span>
                <span style={{
                    color: color || 'var(--accent)',
                    fontWeight: 800,
                    fontSize: '20px',
                    lineHeight: 1,
                }}>
                    {value}
                </span>
            </div>
        </div>
    );

    return (
        <header style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            height: '80px',
            background: 'rgba(27, 28, 29, 0.8)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)',
            flexShrink: 0,
        }}>
            <StatChip label="Total Drones" value={totalDrones} icon="grid_view" />
            <StatChip label="Connected" value={connectedDrones} icon="link" color="var(--accent)" />
            <StatChip label="Flying/Armed" value={activeDrones} icon="flight_takeoff"
                color={activeDrones > 0 ? 'var(--warning)' : 'var(--accent)'} />
            <StatChip label="Avg Battery" value={`${Math.round(avgBattery)}%`} icon="battery_charging_full"
                iconColor="var(--accentSecondary)"
                color={avgBattery < 20 ? 'var(--danger)' : avgBattery < 50 ? 'var(--warning)' : 'var(--accentSecondary)'} />
            <StatChip label="Low Battery" value={lowBatteryDrones} icon="report_problem"
                iconColor="var(--danger)"
                color={lowBatteryDrones > 0 ? 'var(--danger)' : 'var(--textSecondary)'} />
            <StatChip label="GPS Issues" value={gpsIssues} icon="satellite_alt"
                iconColor="var(--textSecondary)"
                color={gpsIssues > 0 ? 'var(--warning)' : 'var(--textPrimary)'} />
        </header>
    );
}

export default React.memo(FleetOverviewDashboard);
